import React from 'react';
import { File, Image as ImageIcon, FileText, Video, Music, Archive } from 'lucide-react';

// Decode filename from various encodings
export const decodeFileName = (filename) => {
  if (!filename) return '';
  
  try {
    let decoded = filename;
    
    // Log the original filename for debugging
    console.log('Original filename:', filename);
    console.log('Filename type:', typeof filename);
    console.log('Filename length:', filename.length);
    
    // Handle URL encoding (most common case)
    if (filename.includes('%')) {
      try {
        decoded = decodeURIComponent(filename);
        console.log('After URL decode:', decoded);
      } catch (urlError) {
        console.warn('URL decode failed, trying alternative methods:', urlError);
      }
    }
    
    // Handle potential double encoding
    if (decoded.includes('%') && decoded !== filename) {
      try {
        decoded = decodeURIComponent(decoded);
        console.log('After double URL decode:', decoded);
      } catch (doubleUrlError) {
        console.warn('Double URL decode failed:', doubleUrlError);
      }
    }
    
    // Handle HTML entities
    if (decoded.includes('&')) {
      try {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = decoded;
        decoded = textarea.value;
        console.log('After HTML entity decode:', decoded);
      } catch (htmlError) {
        console.warn('HTML entity decode failed:', htmlError);
      }
    }
    
    // Handle potential Unicode escape sequences
    if (decoded.includes('\\u') || decoded.includes('\\x')) {
      try {
        decoded = decoded.replace(/\\u([0-9a-fA-F]{4})/g, (match, hex) => {
          return String.fromCharCode(parseInt(hex, 16));
        });
        decoded = decoded.replace(/\\x([0-9a-fA-F]{2})/g, (match, hex) => {
          return String.fromCharCode(parseInt(hex, 16));
        });
        console.log('After Unicode decode:', decoded);
      } catch (unicodeError) {
        console.warn('Unicode decode failed:', unicodeError);
      }
    }
    
    // Handle potential base64 encoding (though unlikely for filenames)
    if (decoded.match(/^[A-Za-z0-9+/]*={0,2}$/) && decoded.length % 4 === 0) {
      try {
        const base64Decoded = atob(decoded);
        // Check if the decoded result looks like a valid filename
        if (base64Decoded.length > 0 && base64Decoded.length < 255 && !base64Decoded.includes('\0')) {
          decoded = base64Decoded;
          console.log('After base64 decode:', decoded);
        }
      } catch (base64Error) {
        console.warn('Base64 decode failed:', base64Error);
      }
    }
    
    // Fix UTF-8 encoding issues (like à¸ characters)
    // These characters appear when UTF-8 bytes are incorrectly interpreted as Latin-1
    if (decoded.includes('à¸') || decoded.includes('à¹') || decoded.includes('à¸£') || decoded.includes('à¸´')) {
      try {
        // Convert the string to a buffer using Latin-1 encoding (which preserves the byte values)
        const latin1Buffer = new Uint8Array(decoded.length);
        for (let i = 0; i < decoded.length; i++) {
          latin1Buffer[i] = decoded.charCodeAt(i);
        }
        // Then convert back to string using UTF-8
        const utf8Decoder = new TextDecoder('utf-8');
        const corrected = utf8Decoder.decode(latin1Buffer);
        if (corrected !== decoded && corrected.length > 0) {
          decoded = corrected;
          console.log('After UTF-8 encoding correction:', decoded);
        }
      } catch (utf8Error) {
        console.warn('UTF-8 encoding correction failed:', utf8Error);
      }
    }
    
    // Final check: if the decoded result is significantly different and looks more like a filename
    if (decoded !== filename && decoded.length > 0 && decoded.length < 255) {
      console.log('Final decoded filename:', decoded);
      return decoded;
    }
    
    // If no significant changes were made, return the original
    return filename;
  } catch (error) {
    console.error('Error decoding filename:', error);
    return filename;
  }
};

// Format file size in human-readable format
export const formatFileSize = (bytes, t) => {
  if (bytes === 0) return `0 ${t?.('Notes.bytes') || 'Bytes'}`;
  const k = 1024;
  const sizes = [
    t?.('Notes.bytes') || 'Bytes', 
    t?.('Notes.kb') || 'KB', 
    t?.('Notes.mb') || 'MB', 
    t?.('Notes.gb') || 'GB'
  ];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Check if file type is viewable in browser
export const isViewableFileType = (mimeType) => {
  if (!mimeType) return false;
  
  const viewableTypes = [
    // Images
    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
    // Documents
    'application/pdf',
    // Text files
    'text/plain', 'text/html', 'text/css', 'text/javascript', 'text/xml',
    'application/json', 'application/xml',
    // Code files
    'text/csv', 'text/markdown', 'text/yaml', 'text/yml',
    // Audio/Video (some browsers can play these)
    'audio/mpeg', 'audio/wav', 'audio/ogg',
    'video/mp4', 'video/webm', 'video/ogg'
  ];
  
  return viewableTypes.includes(mimeType.toLowerCase());
};

// Check if file is an image
export const isImageFile = (mimeType) => {
  if (!mimeType) return false;
  return mimeType.toLowerCase().startsWith('image/');
};

// Get file type icon component
export const getFileTypeIcon = (mimeType, className = 'w-6 h-6') => {
  if (!mimeType) return <File className={className} />;
  
  const mime = mimeType.toLowerCase();
  
  if (mime.startsWith('image/')) {
    return <ImageIcon className={className} />;
  } else if (mime.startsWith('video/')) {
    return <Video className={className} />;
  } else if (mime.startsWith('audio/')) {
    return <Music className={className} />;
  } else if (mime === 'application/pdf' || mime.includes('document') || mime.includes('word') || mime.includes('excel') || mime.includes('powerpoint')) {
    return <FileText className={className} />;
  } else if (mime.includes('zip') || mime.includes('rar') || mime.includes('archive') || mime.includes('compressed')) {
    return <Archive className={className} />;
  } else {
    return <File className={className} />;
  }
};

// Parse attachment tags
export const parseAttachmentTags = (tags) => {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags.filter(Boolean);
  if (typeof tags === 'string') {
    return tags.split(',').map(t => t.trim()).filter(Boolean);
  }
  return [];
};

// Get image preview URL
export const getImagePreviewUrl = (attachmentId, getProtectedTenantCode) => {
  const tenantCode = getProtectedTenantCode();
  const baseUrl = `/api/attachments/view/${attachmentId}?t=${Date.now()}`;
  // Note: Authorization is handled by browser cookies/headers automatically
  return baseUrl;
};

// Fetch attachments for a note
export const fetchAttachments = async (noteId, getProtectedTenantCode, makeFetch, setAttachments) => {
  try {
    const tenantCode = getProtectedTenantCode();
    const response = await makeFetch(`/api/attachments/SalesNote/${noteId}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
      }
    });

    if (response.ok) {
      const data = await response.json();
      
      // Debug: Log the raw attachment data to see what we're getting from the server
      console.log('Raw attachment data from server:', data.attachments);
      if (data.attachments && data.attachments.length > 0) {
        data.attachments.forEach((attachment, index) => {
          console.log(`Attachment ${index}:`, {
            AttachmentID: attachment.AttachmentID,
            OriginalFileName: attachment.OriginalFileName,
            FileName: attachment.FileName,
            Title: attachment.Title,
            'OriginalFileName type': typeof attachment.OriginalFileName,
            'FileName type': typeof attachment.FileName,
            'Title type': typeof attachment.Title
          });
        });
      }
      
      setAttachments(prev => ({
        ...prev,
        [noteId]: data.attachments || []
      }));
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      console.warn('fetchAttachments aborted due to tenant switch', noteId);
    } else {
      console.error('Error fetching attachments:', error);
    }
  }
};

// Upload attachments for a note
export const handleAttachmentUpload = async (noteId, files, getProtectedTenantCode, t, setError, setUploadingAttachments, fetchAttachments) => {
  if (!files || files.length === 0) return;

  setUploadingAttachments(prev => ({ ...prev, [noteId]: true }));

  try {
    const tenantCode = getProtectedTenantCode();
    const selectedRole = localStorage.getItem('selectedRole');
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', decodeFileName(file.name));
      formData.append('description', `${t('Notes.attachmentForSalesNote') || 'Attachment for note'} ${noteId}`);
      formData.append('tags', 'sales-note,attachment');

      const response = await fetch(`/api/attachments/SalesNote/${noteId}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {}),
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `${t('Notes.failedToUpload') || 'Failed to upload'} ${decodeFileName(file.name)}`);
      }
    }

    // Refresh attachments after upload
    await fetchAttachments(noteId);
  } catch (error) {
    console.error('Error uploading attachments:', error);
    const errorMessage = `${t('Notes.failedToUploadAttachments') || 'Failed to upload attachments'}: ${error.message}`;
    setError(errorMessage);
    // Re-throw error so caller can handle it
    throw error;
  } finally {
    setUploadingAttachments(prev => ({ ...prev, [noteId]: false }));
  }
};

// Download attachment
export const handleDownloadAttachment = async (attachment, getProtectedTenantCode, t, setError) => {
  try {
    const tenantCode = getProtectedTenantCode();
    const response = await fetch(`/api/attachments/download/${attachment.AttachmentID}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
      }
    });

    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = decodeFileName(attachment.OriginalFileName || attachment.FileName);
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } else {
      throw new Error(t('Notes.failedToDownloadFile') || 'Failed to download file');
    }
  } catch (error) {
    console.error('Error downloading attachment:', error);
    setError(`${t('Notes.failedToDownloadAttachment') || 'Failed to download attachment'}: ${error.message}`);
  }
};

// Delete attachment
export const handleDeleteAttachment = async (attachment, noteId, getProtectedTenantCode, t, setError, setAttachments, setDeletingAttachments) => {
  const fileName = decodeFileName(attachment.OriginalFileName || attachment.FileName);
  if (!window.confirm(t('Notes.confirmDeleteAttachment') || `Are you sure you want to delete "${fileName}"?`)) {
    return;
  }

  setDeletingAttachments(prev => ({ ...prev, [attachment.AttachmentID]: true }));

  try {
    const tenantCode = getProtectedTenantCode();
    const selectedRole = localStorage.getItem('selectedRole');
    const response = await fetch(`/api/attachments/${attachment.AttachmentID}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {}),
        ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
      }
    });

    if (response.ok) {
      // Remove the attachment from local state
      setAttachments(prev => ({
        ...prev,
        [noteId]: prev[noteId].filter(att => att.AttachmentID !== attachment.AttachmentID)
      }));
      setError(null);
    } else {
      const errorData = await response.json();
      throw new Error(errorData.error || errorData.message || 'Failed to delete attachment');
    }
  } catch (error) {
    console.error('Error deleting attachment:', error);
    setError(`${t('Notes.failedToDeleteAttachment') || 'Failed to delete attachment'}: ${error.message}`);
  } finally {
    setDeletingAttachments(prev => ({ ...prev, [attachment.AttachmentID]: false }));
  }
};

// Load image as blob for gallery view
export const loadImageAsBlob = async (attachmentId, getProtectedTenantCode, imageBlobUrls, imageLoading, imageLoadErrors, setImageBlobUrls, setImageLoading, setImageLoadErrors) => {
  // Check if we already have a blob URL for this attachment
  if (imageBlobUrls[attachmentId]) {
    return imageBlobUrls[attachmentId];
  }

  // Check if already loading
  if (imageLoading[attachmentId]) {
    return null;
  }

  // Check if already errored
  if (imageLoadErrors[attachmentId]) {
    return null;
  }

  setImageLoading(prev => ({ ...prev, [attachmentId]: true }));

  try {
    const tenantCode = getProtectedTenantCode();
    const response = await fetch(`/api/attachments/view/${attachmentId}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
      }
    });

    if (response.ok) {
      const blob = await response.blob();
      if (blob.size > 0) {
        const blobUrl = window.URL.createObjectURL(blob);
        setImageBlobUrls(prev => ({ ...prev, [attachmentId]: blobUrl }));
        setImageLoading(prev => {
          const newState = { ...prev };
          delete newState[attachmentId];
          return newState;
        });
        return blobUrl;
      } else {
        throw new Error('Empty blob');
      }
    } else {
      console.warn('Failed to load image:', response.status);
      setImageLoadErrors(prev => ({ ...prev, [attachmentId]: true }));
      setImageLoading(prev => {
        const newState = { ...prev };
        delete newState[attachmentId];
        return newState;
      });
      return null;
    }
  } catch (error) {
    console.error('Error loading image as blob:', error);
    setImageLoadErrors(prev => ({ ...prev, [attachmentId]: true }));
    setImageLoading(prev => {
      const newState = { ...prev };
      delete newState[attachmentId];
      return newState;
    });
    return null;
  }
};

// View attachment in modal
export const handleViewAttachment = async (attachment, getProtectedTenantCode, t, setError, setViewingAttachment, setSelectedAttachmentForView, setAttachmentViewUrl, setShowViewAttachmentModal) => {
  console.log('🔍 Starting to view attachment:', {
    attachmentId: attachment.AttachmentID,
    fileName: attachment.OriginalFileName || attachment.FileName,
    mimeType: attachment.MimeType,
    fileSize: attachment.FileSize
  });

  setViewingAttachment(true);
  setSelectedAttachmentForView(attachment);
  setError(null);

  try {
    const tenantCode = getProtectedTenantCode();
    console.log('🔍 Using tenant code:', tenantCode);
    
    const response = await fetch(`/api/attachments/view/${attachment.AttachmentID}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
      }
    });

    console.log('🔍 Response status:', response.status);
    console.log('🔍 Response headers:', Object.fromEntries(response.headers.entries()));

    if (response.ok) {
      const blob = await response.blob();
      console.log('🔍 Blob received:', {
        size: blob.size,
        type: blob.type
      });
      
      // Check if the blob is valid
      if (blob.size === 0) {
        throw new Error('Attachment file is empty or corrupted');
      }
      
      // For images, try to validate the blob (temporarily disabled for debugging)
      if (attachment.MimeType && attachment.MimeType.startsWith('image/')) {
        console.log('🔍 Skipping image blob validation for debugging...');
        // Temporarily disabled validation to test if this is causing the issue
      }
      
      const url = window.URL.createObjectURL(blob);
      console.log('🔍 Created blob URL:', url);
      console.log('🔍 Blob details:', {
        size: blob.size,
        type: blob.type,
        lastModified: blob.lastModified
      });
      setAttachmentViewUrl(url);
      setShowViewAttachmentModal(true);
    } else {
      const errorText = await response.text();
      console.error('🔍 Server error response:', errorText);
      throw new Error(`Failed to load attachment: ${response.status} ${response.statusText}`);
    }
  } catch (error) {
    console.error('🔍 Error viewing attachment:', error);
    setError(`${t('Notes.failedToViewAttachment') || 'Failed to view attachment'}: ${error.message}`);
    // Don't show the modal if there's an error
    setShowViewAttachmentModal(false);
  } finally {
    setViewingAttachment(false);
  }
};

// Close view attachment modal
export const handleCloseViewAttachment = (setShowViewAttachmentModal, setSelectedAttachmentForView, attachmentViewUrl, setAttachmentViewUrl, setModalSize) => {
  setShowViewAttachmentModal(false);
  setSelectedAttachmentForView(null);
  if (attachmentViewUrl) {
    window.URL.revokeObjectURL(attachmentViewUrl);
    setAttachmentViewUrl(null);
  }
  // Reset modal size when closing
  if (setModalSize) {
    setModalSize({ width: '90%', height: '85%' });
  }
};
